/* eslint-disable react/no-danger */
import * as React from "react";
import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import SharedComponents from "@audacia-hq/shared/components";
import { formatNumber, formatUserCode } from "@audacia-hq/shared/utils";

import { useAuth } from "../../context/AuthContext";
import { useMember } from "../../context/MemberContext";
import { Expert } from "../../models/expert";
import { GET_DISPLAY_PHONES } from "../../graphql/query";
import { DisplayPhone } from "../../models/ivr";
import { useAppContext } from "../../context/AppContext";
import { useLocale } from "../../context/LocaleContext";
import VisitorSignup from "../consultations/VisitorSignup";
import { useFetcher } from "../../hooks/useClientFetcher";

interface Props {
  expert: Expert;
  visitorMode?: boolean;
}

interface CountryDirectPhones {
  ["PREPAID"]?: DisplayPhone[];
  ["SURTAXED"]?: DisplayPhone[];
}

const DirectCallPhones: React.FC<Props> = ({
  expert, visitorMode,
}) => {
  const { t } = useTranslation(["consultations", "common"]);
  const locale = useLocale();
  const { ipCountry } = useAppContext();
  const { member } = useMember();

  const { signup } = useAuth();
  const [directPhones, setDirectPhones] = useState<{[country: string]: CountryDirectPhones}>({});
  const [directCallCountry, setDirectCallCountry] = useState<string>();
  const [prepaidElts, setPrepaidElts] = useState([]);
  const [surtaxedElts, setSurtaxedElts] = useState([]);
  const [_getDisplayPhones, { data }] = useFetcher<{
    displayPhones: DisplayPhone[]
  }>(GET_DISPLAY_PHONES, {
    variables: {
      usages: ["PREPAID", "SURTAXED"],
      languages: [locale.toUpperCase()],
      countries: ["ANY"],
    },
  });

  useEffect(() => {
    if (!data) return;
    // const byCountry = (surtaxedOnly ? data.displayPhones.filter((v) => v.usage === "SURTAXED") : data.displayPhones)
    const byCountry = data.displayPhones
      .reduce<{[country: string]: CountryDirectPhones}>((aggr, p) => ({
        ...aggr,
        [p.country || "OTHER"]: {
          ...(aggr[p.country || "OTHER"] || {}),
          [p.usage]: [
            ...((aggr[p.country || "OTHER"] || {})[p.usage] || []),
            { ...p, communications: p.communications },
          ],
        },
      }), {});
    setDirectPhones(byCountry);
    setDirectCallCountry(ipCountry && byCountry[ipCountry] ? ipCountry : "OTHER");
  }, [data]);

  useEffect(() => {
    if (!directPhones[directCallCountry]) return;
    setPrepaidElts(directPhones[directCallCountry]?.PREPAID
      ? directPhones[directCallCountry]?.PREPAID.map((phone) => displayPhoneToNode(phone, "PREPAID"))
      : []);
    setSurtaxedElts(directPhones[directCallCountry]?.SURTAXED
      ? directPhones[directCallCountry]?.SURTAXED.map((phone) => displayPhoneToNode(phone, "SURTAXED"))
      : []);
  }, [directCallCountry, directPhones]);

  const accountDisclaimer = (
    // <div className="rounded-xl bg-stone-100 text-sm p-4">
    <Trans
      i18nKey="phone.direct.accountDisclaimer"
      ns="consultations"
      components={[
        <span
          className="font-semibold cursor-pointer"
          onClick={() => signup()}
          onKeyDown={() => signup()}
        >
          phoneNumberTxt
        </span>,
      ]}
    />
    // </div>

  );

  const displayPhoneToNode = (displayPhone: DisplayPhone, phoneType) => {
    const bgColor = phoneType === "PREPAID" ? "bg-slate-100" : "bg-stone-100";
    const bulletPointColor = phoneType === "PREPAID" ? "bg-slate-500" : "bg-stone-500";

    return (
      <div key={`${displayPhone.phoneNumber}-${displayPhone.country}-${displayPhone.usage}`}>
        {
          displayPhone.communications?.unavailable
            ? (
              <div className={`rounded-xl ${bgColor} p-4 my-2 text-center flex flex-col gap-2`}>
                <div>{displayPhone.communications?.unavailable}</div>
                <div>
                  {accountDisclaimer}
                </div>
              </div>
            )
            : (
              <div className={`rounded-xl ${bgColor} ${phoneType !== "PREPAID" || !visitorMode ? "p-4" : ""} flex flex-col mt-2 gap-3`}>
                {displayPhone.communications?.discontinuing && (
                  <div className="text-center text-sm text-gray-500 flex flex-col gap-2">
                    <div>{displayPhone.communications?.discontinuing}</div>
                    <div>
                      {accountDisclaimer}
                    </div>
                  </div>
                )}
                <div className="flex content-start">
                  <div className={`inline-flex text-white ${bulletPointColor} w-6 h-6 aspect-square rounded-full content-center justify-center mr-2`}>
                    1
                  </div>
                  <div>
                    <div>
                      <Trans
                        i18nKey="phone.direct.callNumber"
                        ns="consultations"
                        values={{
                          phoneNumber: formatNumber(
                            displayPhone.phoneNumber,
                            displayPhone.country ? "NATIONAL" : "INTERNATIONAL",
                            displayPhone.country,
                          ),
                          price: displayPhone.communications?.callPrice ? ` (${displayPhone.communications?.callPrice})` : "",
                          type: displayPhone.communications?.callType ? ` (${displayPhone.communications?.callType})` : "",
                          star: displayPhone.communications?.callDisclaimer ? "*" : "",
                        }}
                        components={[
                          <span className="font-semibold">phoneNumberTxt</span>,
                          <sup className="font-semibold">star</sup>,
                        ]}
                      />
                    </div>
                    {displayPhone.communications?.callDisclaimer && (
                      <div className="text-sm text-gray-500">
                        *
                        {displayPhone.communications?.callDisclaimer }
                      </div>
                    )}
                  </div>
                </div>

                {
                  phoneType === "PREPAID" && (
                    <div className="flex content-start">
                      <div className={`inline-flex text-white ${bulletPointColor} w-6 h-6 aspect-square rounded-full content-center justify-center mr-2`}>
                        2
                      </div>
                      <div>
                        <Trans
                          i18nKey="phone.direct.memberCode"
                          ns="consultations"
                          values={{
                            code: member?.profile?.memberCode
                              ? formatUserCode(member.profile.memberCode, { padLength: 8, padChar: "0", format: "4 4" }) : "",
                          }}
                          components={[
                            <span className="font-semibold">memberCode</span>,
                          ]}
                        />
                      </div>

                    </div>
                  )
                }
                <div className="flex content-start">
                  <div className={`inline-flex text-white ${bulletPointColor} w-6 h-6 aspect-square rounded-full content-center justify-center mr-2`}>
                    {phoneType === "PREPAID" ? 3 : 2}
                  </div>
                  <div>
                    <Trans
                      i18nKey="phone.direct.expertCode"
                      ns="consultations"
                      values={{
                        code: String(expert.expertCode).padStart(3, "0"),
                      }}
                      components={[
                        <span className="font-semibold">expertCode</span>,
                      ]}
                    />
                  </div>

                </div>
              </div>
            )
        }
      </div>
    );
  };

  if (!visitorMode) {
    return !Object.keys(directPhones).length ? (<><div className="my-6 font-semibold">{t("phone.direct.noCountrySelect")}</div></>)
      : (
        <div>
          <div className="flex flex-wrap gap-3 text-lg mb-3 justify-center items-center">
            <div className="whitespace-nowrap inline-block">
              {t("phone.direct.directCall")}
            </div>
            <div className="min-w-[14rem]">
              <SharedComponents.Form.Select
                value={directCallCountry}
                onChange={(value) => setDirectCallCountry(value)}
                renderSelected={(value) => t(`common:countries.${value}`)}
                inline
              >
                { Object.keys(directPhones).sort((a, b) => (a === "OTHER" ? 1 : -1)).map((country) => (
                  <SharedComponents.Form.Option
                    key={country}
                    value={country}
                    text={t(`common:countries.${country}`)}
                  />
                ))}

              </SharedComponents.Form.Select>
            </div>
          </div>
          <div className="flex flex-col">

            {(!prepaidElts.length && !surtaxedElts.length) ? (<div className="my-6 font-semibold">{t("phone.direct.noCountrySelect")}</div>)
              : (
                <>
                  {/* {!member && accountDisclaimer} */}
                  {prepaidElts.length > 0
              && (
                <div className={!member ? "order-3" : ""}>
                  <div className="font-semibold my-2">{t("phone.direct.type.prepaid")}</div>
                  {prepaidElts}
                </div>
              )}
                  {surtaxedElts.length > 0
              && (
                <div>
                  <div className="font-semibold my-2" dangerouslySetInnerHTML={{ __html: t("phone.direct.type.surtaxed") }} />
                  {surtaxedElts}
                </div>
              )}
                </>
              )}
          </div>
        </div>
      );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 text-lg mb-3 justify-center items-center">
        <div className="whitespace-nowrap inline-block">
          {t("phone.direct.directCall")}
        </div>
        <div className="min-w-[14rem]">
          <SharedComponents.Form.Select
            value={directCallCountry}
            onChange={(value) => setDirectCallCountry(value)}
            renderSelected={(value) => t(`common:countries.${value}`)}
            inline
          >
            { Object.keys(directPhones).sort((a, b) => (a === "OTHER" ? 1 : -1)).map((country) => (
              <SharedComponents.Form.Option
                key={country}
                value={country}
                text={t(`common:countries.${country}`)}
              />
            ))}

          </SharedComponents.Form.Select>
        </div>
      </div>

      <div className="font-semibold my-2">{t("consultation.visitor.credits")}</div>
      <VisitorSignup prepaidNumberSteps={prepaidElts[0] || undefined} />
      <div className="flex flex-col">

        {surtaxedElts.length > 0
        && (
          <div className="mt-4">
            <div className="font-semibold my-2" dangerouslySetInnerHTML={{ __html: t("phone.direct.type.surtaxed") }} />
            {surtaxedElts}
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectCallPhones;
